/**
 * MetricProgressStages — Barra de progreso con etapas de flujo para tarjetas KPI.
 *
 * QUÉ HACE: pinta la barra de progreso (con etiqueta `%` opcional a la derecha) y
 *   debajo la secuencia de etapas separadas por chevrones (p. ej.
 *   "Recibido › En empaque › Despachado"). La usa "Pedidos Pendientes".
 * QUÉ INVOCA: MetricProgressBar (UI compartida) y el icono ChevronRight.
 * QUÉ EXPORTA: MetricProgressStages.
 * QUÉ NO DEBE HACER: no calcula métricas ni accede a datos.
 */
import { Fragment } from "react";
import { ChevronRight } from "lucide-react";
import { MetricProgressBar } from "./MetricProgressBar";

interface MetricProgressStagesProps {
  /** Porcentaje de avance 0–100. */
  value: number;
  /** Clases Tailwind del relleno de la barra. */
  fillClassName?: string;
  /** Clases Tailwind del alto del carril. */
  heightClassName?: string;
  /** Etiqueta a la derecha de la barra (p. ej. "33%"). */
  trailingLabel?: string;
  /** Etapas del flujo, en orden. */
  stages: string[];
  className?: string;
}

export const MetricProgressStages = ({
  value,
  fillClassName = "bg-blue-500",
  heightClassName = "h-3",
  trailingLabel,
  stages,
  className = "",
}: MetricProgressStagesProps) => {
  return (
    <div className={className}>
      <div className="flex items-start gap-2">
        <div className="min-w-0 flex-1">
          <MetricProgressBar
            value={value}
            fillClassName={fillClassName}
            heightClassName={heightClassName}
            className="w-full"
          />

          <div className="mt-1.5 flex items-center justify-between gap-0.5">
            {stages.map((stage, index) => (
              <Fragment key={`${stage}-${index}`}>
                {index > 0 && (
                  <ChevronRight
                    className="h-2.5 w-2.5 shrink-0 text-slate-300 dark:text-slate-600"
                    aria-hidden="true"
                  />
                )}
                <span className="min-w-0 truncate text-[6px] font-medium text-slate-500 sm:text-[7px] dark:text-slate-400">
                  {stage}
                </span>
              </Fragment>
            ))}
          </div>
        </div>

        {trailingLabel && (
          <span className="mt-0.5 shrink-0 text-[8px] font-semibold tabular-nums text-gray-500 sm:text-[9px] dark:text-slate-400">
            {trailingLabel}
          </span>
        )}
      </div>
    </div>
  );
};
